import {
    LayoutDashboard, Network, FileText, Settings, Database,
    ShieldAlert, Server, History, Users, BarChart3,
    MessageCircle, Eye, Radio, Camera, Banknote, Fingerprint, Share2, GraduationCap, Layers,
    BookOpen, Globe,
    type LucideIcon,
} from 'lucide-react';
import type { FeatureKey } from '../auth/features';

export const SIDEBAR_GROUPS_STORAGE_KEY = 'cryptoflow-sidebar-groups';

export interface SidebarNavItem {
    icon: LucideIcon;
    label: string;
    path: string;
    /** Shown locked (not a link) unless the user holds this feature (see auth/features.ts). */
    feature?: FeatureKey;
}

export interface SidebarGroup {
    id: string;
    label: string;
    defaultExpanded: boolean;
    items: SidebarNavItem[];
}

export const SIDEBAR_GROUPS: SidebarGroup[] = [
    {
        id: 'investigate',
        label: 'Investigate',
        defaultExpanded: true,
        items: [
            { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
            { icon: GraduationCap, label: 'Demos', path: '/demos' },
            { icon: Network, label: 'Workbench', path: '/workbench' },
            { icon: Layers, label: 'Fusion', path: '/fusion', feature: 'investigation.auto' },
            { icon: FileText, label: 'Cases', path: '/cases' },
            { icon: Eye, label: 'Watchlist & Alerts', path: '/watchlist', feature: 'watchlist' },
        ],
    },
    {
        id: 'intelligence',
        label: 'Intelligence',
        // Expanded by default so Instagram / Telegram / P2P sit together and
        // officers do not re-learn a second nav group for the same OSINT job.
        defaultExpanded: true,
        items: [
            { icon: Camera, label: 'Instagram leads', path: '/instagram', feature: 'instagram.osint' },
            { icon: Share2, label: 'Compare IG accounts', path: '/instagram-network', feature: 'instagram.osint' },
            { icon: Users, label: 'P2P Intelligence', path: '/p2p', feature: 'p2p.intel' },
            { icon: Radio, label: 'Telegram Intel', path: '/telegram', feature: 'telegram.intel' },
            { icon: Fingerprint, label: 'Harvest Feed', path: '/harvest-feed', feature: 'mulehunter.feed' },
            { icon: Database, label: 'Entities', path: '/entities' },
            { icon: Banknote, label: 'Off-Ramp', path: '/off-ramp', feature: 'fiat.statementflow' },
            { icon: Globe, label: 'Syndicate Operations', path: '/syndicates', feature: 'mulehunter.syndicates' },
        ],
    },
    {
        id: 'analysis',
        label: 'Analysis',
        defaultExpanded: false,
        items: [
            { icon: BarChart3, label: 'Graph Analytics', path: '/analytics' },
            { icon: Server, label: 'Data Sources', path: '/data-sources', feature: 'scrapers' },
            { icon: ShieldAlert, label: 'Risk Rules', path: '/risk' },
            { icon: History, label: 'Search History', path: '/search-history' },
        ],
    },
];

/** Ungrouped nav items shown as individual links below the collapsible sections. */
export const SIDEBAR_STANDALONE_ITEMS: SidebarNavItem[] = [
    { icon: BookOpen, label: 'Documentation', path: '/documentation' },
    { icon: MessageCircle, label: 'Feedback', path: '/feedback' },
    { icon: Settings, label: 'Settings', path: '/settings' },
];

/** Flat list of all nav items (for collapsed sidebar icon rail). */
export const ALL_SIDEBAR_ITEMS: SidebarNavItem[] = [
    ...SIDEBAR_GROUPS.flatMap(g => g.items),
    ...SIDEBAR_STANDALONE_ITEMS,
];

/**
 * True when the item is gated and the user lacks its feature. Locked items
 * are still rendered — with a lock and no navigation — so trial users can see
 * what the product offers (owner decision, 4 Oct 2026).
 */
export function isNavItemLocked(
    item: SidebarNavItem,
    allowed: (feature: FeatureKey) => boolean,
): boolean {
    return Boolean(item.feature) && !allowed(item.feature as FeatureKey);
}

export function isNavItemActive(pathname: string, path: string): boolean {
    if (path === '/') return pathname === '/';
    return pathname === path || pathname.startsWith(`${path}/`);
}

export function findGroupForPath(pathname: string): string | null {
    for (const group of SIDEBAR_GROUPS) {
        if (group.items.some(item => isNavItemActive(pathname, item.path))) {
            return group.id;
        }
    }
    return null;
}

export function getDefaultGroupExpansion(): Record<string, boolean> {
    return Object.fromEntries(SIDEBAR_GROUPS.map(g => [g.id, g.defaultExpanded]));
}
